import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Search, Package, CheckCircle2, Clock, Truck, 
  FileCheck, MapPin, ExternalLink, Plane, Ship,
  AlertCircle, Loader2, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Carrier tracking URL patterns
const CARRIER_LINKS = {
  // Airlines
  'AF': { name: 'Air France Cargo', url: 'https://www.afklcargo.com/mycargo/shipment/' },
  'BA': { name: 'British Airways Cargo', url: 'https://www.iagcargo.com/en/track-trace?awb=' },
  'LH': { name: 'Lufthansa Cargo', url: 'https://lufthansa-cargo.com/tracking?awb=' },
  'KL': { name: 'KLM Cargo', url: 'https://www.afklcargo.com/mycargo/shipment/' },
  'EK': { name: 'Emirates SkyCargo', url: 'https://www.skycargo.com/track/?mode=awb&awb=' },
  'QR': { name: 'Qatar Airways Cargo', url: 'https://www.qrcargo.com/s/track-shipment?awbPrefix=157&awbNumber=' },
  'SQ': { name: 'Singapore Airlines Cargo', url: 'https://www.siacargo.com/tracking.asp?prefix=618&no=' },
  // Shipping lines
  'MAEU': { name: 'Maersk', url: 'https://www.maersk.com/tracking/' },
  'MSCU': { name: 'MSC', url: 'https://www.msc.com/track-a-shipment?agencyPath=msc&link=' },
  'CMDU': { name: 'CMA CGM', url: 'https://www.cma-cgm.com/ebusiness/tracking/search?SearchBy=BL&Reference=' },
  'HLCU': { name: 'Hapag-Lloyd', url: 'https://www.hapag-lloyd.com/en/online-business/track/track-by-booking-solution.html?blno=' },
  'COSU': { name: 'COSCO', url: 'https://elines.coscoshipping.com/ebusiness/cargotracking?trackingType=BOOKING&number=' },
  'EGLV': { name: 'Evergreen', url: 'https://www.shipmentlink.com/servlet/TDB1_CargoTracking.do?BLNo=' },
  'ONEY': { name: 'ONE', url: 'https://ecomm.one-line.com/one-ecom/manage-shipment/cargo-tracking?trakNoParam=' },
  // Couriers
  'DHL': { name: 'DHL', url: 'https://www.dhl.com/en/express/tracking.html?AWB=' },
  'FEDEX': { name: 'FedEx', url: 'https://www.fedex.com/fedextrack/?trknbr=' },
  'UPS': { name: 'UPS', url: 'https://www.ups.com/track?tracknum=' },
  'TNT': { name: 'TNT', url: 'https://www.tnt.com/express/en_us/site/tracking.html?searchType=con&cons=' },
};

const TrackingPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('elementary');
  
  // Elementary tracking state
  const [referenceNumber, setReferenceNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [trackingResult, setTrackingResult] = useState(null);
  const [error, setError] = useState('');

  // Carrier tracking state
  const [carrierNumber, setCarrierNumber] = useState('');
  const [detectedCarrier, setDetectedCarrier] = useState(null);

  // Detect carrier from tracking number
  const detectCarrier = (number) => {
    const cleaned = number.toUpperCase().replace(/[^A-Z0-9]/g, '');
    
    // AWB format: 3-digit prefix + 8-digit number (e.g., 074-12345678)
    if (/^\d{3}\d{8}$/.test(cleaned) || /^\d{3}-\d{8}$/.test(number)) {
      return { type: 'air', prefix: cleaned.substring(0, 3) };
    }
    
    // Container/BL format: 4-letter prefix + numbers
    const blMatch = cleaned.match(/^([A-Z]{4})/);
    if (blMatch && CARRIER_LINKS[blMatch[1]]) {
      return { type: 'sea', carrier: CARRIER_LINKS[blMatch[1]], code: blMatch[1] };
    }
    
    // Check for known courier patterns
    if (cleaned.startsWith('1Z')) return { type: 'courier', carrier: CARRIER_LINKS['UPS'], code: 'UPS' };
    if (/^\d{10,}$/.test(cleaned)) return { type: 'courier', carrier: CARRIER_LINKS['DHL'], code: 'DHL' };
    
    return null;
  };

  const handleCarrierNumberChange = (value) => {
    setCarrierNumber(value);
    if (value.length >= 4) {
      const detected = detectCarrier(value);
      setDetectedCarrier(detected);
    } else {
      setDetectedCarrier(null);
    }
  };

  const handleElementaryTrack = async () => {
    if (!referenceNumber.trim()) {
      toast.error('Please enter a reference number');
      return;
    }

    setLoading(true);
    setError('');
    setTrackingResult(null);

    try {
      const response = await axios.get(`${API}/tracking/${referenceNumber}`, {
        params: { email: email || undefined }
      });
      setTrackingResult(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError(t('trackingPage.shipmentNotFoundDesc'));
      } else {
        setError('Unable to fetch tracking information. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCarrierTrack = () => {
    if (!carrierNumber.trim()) {
      toast.error('Please enter a tracking number');
      return;
    }

    const detected = detectCarrier(carrierNumber);
    if (detected?.carrier?.url) {
      window.open(detected.carrier.url + carrierNumber.replace(/[^A-Z0-9]/gi, ''), '_blank');
    } else {
      toast.error('Could not detect carrier. Please select manually below.');
    }
  };

  const milestones = [
    { status: 'booked', label: t('trackingPage.milestones.booked'), icon: FileCheck },
    { status: 'in_transit', label: t('trackingPage.milestones.inTransit'), icon: Truck },
    { status: 'customs', label: t('trackingPage.milestones.customs'), icon: FileCheck },
    { status: 'out_for_delivery', label: t('trackingPage.milestones.outForDelivery'), icon: MapPin },
    { status: 'delivered', label: t('trackingPage.milestones.delivered'), icon: CheckCircle2 },
  ];

  const getStatusIndex = (status) => {
    const index = milestones.findIndex(m => m.status === status);
    return index >= 0 ? index : 0;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tracking-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-slate-900">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-30" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="text-cyan text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('trackingPage.badge')}
            </span>
            <h1 className="font-space text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t('trackingPage.title')}
            </h1>
            <p className="text-slate-400 text-lg">
              {t('trackingPage.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tracking Section */}
      <section className="py-12">
        <div className="container-custom">
          <Card className="max-w-3xl mx-auto shadow-xl border-0 rounded-2xl overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full rounded-none border-b bg-slate-50 p-0 h-auto">
                <TabsTrigger 
                  value="elementary" 
                  className="flex-1 py-4 rounded-none data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-electric data-[state=active]:shadow-none"
                >
                  <Package className="w-4 h-4 mr-2" />
                  {t('trackingPage.elementaryTab')}
                </TabsTrigger>
                <TabsTrigger 
                  value="carrier" 
                  className="flex-1 py-4 rounded-none data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-electric data-[state=active]:shadow-none"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {t('trackingPage.carrierTab')}
                </TabsTrigger>
              </TabsList>

              {/* Elementary Cargo Tracking */}
              <TabsContent value="elementary" className="p-0 mt-0">
                <CardContent className="p-8">
                  <div className="space-y-6">
                    <div>
                      <Label className="text-slate-700 font-medium">{t('trackingPage.referenceNumber')}</Label>
                      <Input
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                        placeholder={t('trackingPage.referencePlaceholder')}
                        className="mt-2 h-12 border-slate-200 focus:border-electric focus:ring-electric/20"
                        data-testid="reference-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 font-medium">{t('trackingPage.emailOptional')}</Label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('trackingPage.emailPlaceholder')}
                        className="mt-2 h-12 border-slate-200 focus:border-electric focus:ring-electric/20"
                        data-testid="email-input"
                      />
                    </div>
                    <Button
                      onClick={handleElementaryTrack}
                      disabled={loading}
                      className="w-full bg-electric hover:bg-electric/90 text-white h-12 rounded-lg"
                      data-testid="track-btn"
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('trackingPage.searching')}</>
                      ) : (
                        <><Search className="w-4 h-4 mr-2" /> {t('trackingPage.trackShipment')}</>
                      )}
                    </Button>
                  </div>

                  {/* Error State */}
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
                    >
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-800 font-medium">{t('trackingPage.shipmentNotFound')}</p>
                        <p className="text-red-600 text-sm mt-1">{error}</p>
                      </div>
                    </motion.div>
                  )}

                  {/* Tracking Result */}
                  {trackingResult && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-8 space-y-6"
                    >
                      {/* Status Overview */}
                      <div className="bg-slate-50 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <p className="text-sm text-slate-500">{t('trackingPage.reference')}</p>
                            <p className="font-space font-bold text-slate-900">{trackingResult.reference}</p>
                          </div>
                          <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
                            trackingResult.status === 'delivered' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-cyan/20 text-cyan-800'
                          }`}>
                            {trackingResult.status_label || trackingResult.status}
                          </div>
                        </div>
                        
                        {trackingResult.origin && trackingResult.destination && (
                          <div className="flex items-center gap-4 text-sm text-slate-600">
                            <span>{trackingResult.origin}</span>
                            <ArrowRight className="w-4 h-4" />
                            <span>{trackingResult.destination}</span>
                          </div>
                        )}
                      </div>

                      {/* Milestone Progress */}
                      <div className="py-4">
                        <div className="flex items-center justify-between">
                          {milestones.map((milestone, index) => {
                            const currentIndex = getStatusIndex(trackingResult.status);
                            const isCompleted = index <= currentIndex;
                            const isCurrent = index === currentIndex;
                            
                            return (
                              <div key={milestone.status} className="flex flex-col items-center flex-1">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                                  isCompleted 
                                    ? 'bg-cyan text-slate-900' 
                                    : 'bg-slate-200 text-slate-400'
                                } ${isCurrent ? 'ring-4 ring-cyan/30' : ''}`}>
                                  <milestone.icon className="w-5 h-5" />
                                </div>
                                <span className={`text-xs text-center ${
                                  isCompleted ? 'text-slate-900 font-medium' : 'text-slate-400'
                                }`}>
                                  {milestone.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {/* Progress bar */}
                        <div className="relative mt-4">
                          <div className="h-1 bg-slate-200 rounded-full" />
                          <div 
                            className="absolute top-0 left-0 h-1 bg-cyan rounded-full transition-all"
                            style={{ width: `${(getStatusIndex(trackingResult.status) / (milestones.length - 1)) * 100}%` }}
                          />
                        </div>
                      </div>

                      {/* Latest Update */}
                      {trackingResult.latest_update && (
                        <div className="border-l-4 border-cyan pl-4">
                          <p className="text-sm text-slate-500">{trackingResult.latest_update.date}</p>
                          <p className="text-slate-900 font-medium">{trackingResult.latest_update.message}</p>
                        </div>
                      )}

                      {/* Contact Info */}
                      <div className="bg-slate-900 rounded-xl p-6 text-center">
                        <p className="text-slate-400 text-sm mb-2">{t('trackingPage.questionsAboutShipment')}</p>
                        <p className="text-white">
                          {t('trackingPage.contactHandler')}{' '}
                          <a href="mailto:info@elementarycargo.com" className="text-cyan hover:underline">
                            info@elementarycargo.com
                          </a>
                        </p>
                      </div>
                    </motion.div>
                  )}
                </CardContent>
              </TabsContent>

              {/* Carrier Tracking */}
              <TabsContent value="carrier" className="p-0 mt-0">
                <CardContent className="p-8">
                  <p className="text-slate-600 mb-6">
                    {t('trackingPage.carrierTrackingIntro')}
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-700 font-medium">{t('trackingPage.trackingNumber')}</Label>
                      <Input
                        value={carrierNumber}
                        onChange={(e) => handleCarrierNumberChange(e.target.value)}
                        placeholder={t('trackingPage.trackingNumberPlaceholder')}
                        className="mt-2 h-12 border-slate-200 focus:border-electric focus:ring-electric/20"
                        data-testid="carrier-number-input"
                      />
                    </div>

                    {detectedCarrier && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2 text-sm text-cyan"
                      >
                        {detectedCarrier.type === 'air' && <Plane className="w-4 h-4" />}
                        {detectedCarrier.type === 'sea' && <Ship className="w-4 h-4" />}
                        {detectedCarrier.type === 'courier' && <Truck className="w-4 h-4" />}
                        <span>
                          {t('trackingPage.detected')}: {detectedCarrier.carrier?.name || `Air Waybill (Prefix: ${detectedCarrier.prefix})`}
                        </span>
                      </motion.div>
                    )}

                    <Button
                      onClick={handleCarrierTrack}
                      className="w-full bg-electric hover:bg-electric/90 text-white h-12 rounded-lg"
                      data-testid="carrier-track-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" /> {t('trackingPage.trackOnCarrierSite')}
                    </Button>
                  </div>

                  {/* Quick Links */}
                  <div className="mt-8">
                    <h3 className="font-semibold text-slate-900 mb-4">{t('trackingPage.quickLinks')}</h3>
                    
                    {/* Shipping Lines */}
                    <div className="mb-6">
                      <p className="text-sm text-slate-500 mb-3 flex items-center gap-2">
                        <Ship className="w-4 h-4" /> {t('trackingPage.oceanFreightCarriers')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {['MAEU', 'MSCU', 'CMDU', 'HLCU', 'COSU', 'ONEY'].map((code) => (
                          <a
                            key={code}
                            href={CARRIER_LINKS[code].url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-700 transition-colors"
                          >
                            {CARRIER_LINKS[code].name}
                          </a>
                        ))}
                      </div>
                    </div>

                    {/* Airlines */}
                    <div className="mb-6">
                      <p className="text-sm text-slate-500 mb-3 flex items-center gap-2">
                        <Plane className="w-4 h-4" /> {t('trackingPage.airFreightCarriers')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {['LH', 'AF', 'KL', 'EK', 'QR', 'SQ'].map((code) => (
                          <a
                            key={code}
                            href={CARRIER_LINKS[code].url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-700 transition-colors"
                          >
                            {CARRIER_LINKS[code].name}
                          </a>
                        ))}
                      </div>
                    </div>

                    {/* Couriers */}
                    <div>
                      <p className="text-sm text-slate-500 mb-3 flex items-center gap-2">
                        <Truck className="w-4 h-4" /> {t('trackingPage.couriers')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {['DHL', 'FEDEX', 'UPS', 'TNT'].map((code) => (
                          <a
                            key={code}
                            href={CARRIER_LINKS[code].url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-700 transition-colors"
                          >
                            {CARRIER_LINKS[code].name}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </TabsContent>
            </Tabs>
          </Card>

          {/* Help text */}
          <div className="max-w-3xl mx-auto mt-6 text-center text-sm text-slate-500">
            <p>
              {t('trackingPage.needHelpTracking')}{' '}
              <a href="mailto:info@elementarycargo.com" className="text-electric hover:underline">
                {t('trackingPage.contactUsAssist')}
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrackingPage;
